import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, WifiOff } from "lucide-react";
import { useAuth, canSimulate } from "../lib/auth";
import { useScrollLock, useFitViewport, useNavFold } from "../lib/menus";
import { netErrorStore } from "../lib/api";
import { fmtDate } from "../lib/format";
import BottomNav from "./BottomNav";
import StoreSwitcher, { useGroupStores } from "./StoreSwitcher";
import NavMore from "./NavMore";
import { InstallBanner, InstallButton, IosInstallSheet } from "./Pwa";
import { trialEnding } from "./TrialBanner";

const IDLE_MS = 30 * 60 * 1000;

function StaffShell({ store, logout, children }) {
  const out = () => { logout(); window.location.replace("/personale"); };
  useEffect(() => {
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => { if (localStorage.getItem("jatak_staff_code")) out(); }, IDLE_MS); };
    const evs = ["pointerdown", "keydown", "touchstart"];
    evs.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, reset)); };
  });
  return (
    <div className="App staff-mode">
      <header className="topbar staff-bar" data-testid="staff-header">
        <div className="in">
          <span className="brand"><img className="brand-ico" src="/logo-mark.svg?v=2" alt="" width="30" height="30" />
            <span data-testid="staff-header-name">{store.staff.name} · {store.name}</span></span>
          <div className="staff-bar-btns">
            <button type="button" className="btn small" onClick={out} data-testid="staff-switch-user">Skift medarbejder</button>
            <button type="button" className="btn small" onClick={out} data-testid="staff-logout">Log ud</button>
          </div>
        </div>
      </header>
      <main className="container">{children}</main>
    </div>
  );
}

const NAV = [
  ["/overblik", "Overblik"],
  ["/offers/new", "Nyt tilbud"],
  ["/pickup", "Udlevering"],
  ["/customers", "Kunder"],
  ["/stats", "Statistik"],
];

function secondaryItems(store, group, multi) {
  const fbErr = store?.fb_health?.status === "error";
  return [
    { href: "/settings", label: "Indstillinger", id: "settings", testid: "nav-Indstillinger", dot: fbErr },
    { href: "/billing", label: "Abonnement", id: "billing", testid: "nav-billing" },
    group && { href: "/butikker", label: multi ? "Butikker" : "+ Tilføj butik", id: "stores", testid: "nav-stores" },
    canSimulate(store) && { href: "/simulator", label: "Simulator", id: "simulator", testid: "nav-Simulator" },
    { href: "/support", label: "Support", id: "support", testid: "nav-support" },
    { href: "/guide/JaTak-Pro-guide.pdf", label: "Guide (PDF)", id: "guide", testid: "nav-guide", external: true },
  ].filter(Boolean);
}

function Banner({ store, path }) {
  const a = store.access || {};
  if (a.kind === "trial" && !a.subscribed && a.daysLeft && !(trialEnding(a) && path === "/overblik")) {
    return (
      <div className="banner" data-testid="trial-banner">
        Gratis prøveperiode – {a.daysLeft} {a.daysLeft === 1 ? "dag" : "dage"} tilbage.{" "}
        {a.started ? <>Abonnementet starter {fmtDate(a.until)} – <Link to="/billing">se faktura</Link>.</> : <><Link to="/billing">Start abonnement</Link> for at fortsætte efter {fmtDate(a.until)}.</>}
      </div>
    );
  }
  return null;
}

function NetErrorState({ onRetry }) {
  return (
    <div className="empty net-error" role="alert" data-testid="net-error-state">
      <WifiOff size={36} />
      <h2>{navigator.onLine ? "Kunne ikke hente data – tjek din forbindelse" : "Kunne ikke hente data – du er offline"}</h2>
      <p>Siden opdaterer automatisk, når forbindelsen er tilbage.</p>
      <button className="btn primary" onClick={onRetry} data-testid="net-error-retry">
        Prøv igen
      </button>
    </div>
  );
}

export default function Layout({ children }) {
  const { store, logout } = useAuth();
  if (store?.staff) return <StaffShell store={store} logout={logout}>{children}</StaffShell>;
  return <OwnerLayout>{children}</OwnerLayout>;
}

function OwnerLayout({ children }) {
  const { store, logout, stopImpersonate, retry } = useAuth();
  const netError = useSyncExternalStore(netErrorStore.subscribe, netErrorStore.get);
  const loc = useLocation();
  const nav = useNavigate();
  const active = loc.pathname;
  const isActive = (href) => active.startsWith(href);
  const isAdmin = !!store?.is_owner;
  const impersonating = !!store?.impersonated;
  const [menuOpen, setMenuOpen] = useState(false);
  const group = useGroupStores();
  const multi = !isAdmin && group?.stores?.length > 1;
  const barRef = useRef(null);
  const navRef = useRef(null);
  const secondary = isAdmin ? [] : secondaryItems(store, group, multi);
  const doLogout = () => {
    logout();
    nav("/");
  };
  const fold = useNavFold(barRef, [store?.name, group?.stores?.length || 0, isAdmin, store?.test_mode ? 1 : 0, secondary.length]);
  useScrollLock(menuOpen);
  useFitViewport(navRef, menuOpen);

  useEffect(() => {
    setMenuOpen(false);
    netErrorStore.clear();
  }, [active]);

  useEffect(() => {
    if (!menuOpen) return;
    const esc = (e) => e.key === "Escape" && setMenuOpen(false);
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [menuOpen]);

  return (
    <div className={isAdmin ? "App" : "App has-bottom-nav"}>
      {impersonating && (
        <div className="imp-bar" data-testid="impersonation-bar">
          <span>
            <strong>Support-visning:</strong> du ser systemet som <b>{store?.name}</b>.
          </span>
          <button
            className="imp-exit"
            data-testid="impersonation-exit"
            onClick={async () => {
              await stopImpersonate();
              nav("/owner");
            }}
          >
            Tilbage til admin
          </button>
        </div>
      )}
      <header className={"topbar" + (fold ? " fold" : "") + (isAdmin ? "" : " has-more")} data-fold={fold ? "1" : "0"}>
        <div className="in" ref={barRef}>
          <Link className="brand" to={isAdmin ? "/owner" : "/overblik"} data-testid="brand-link">
            <img className="brand-ico" src="/logo-mark.svg?v=2" alt="" width="30" height="30" />
            {multi ? <span className="sr-only">{store.name}</span> : isAdmin ? "Admin" : store?.name || "JaTak Pro"}
          </Link>
          {multi && <StoreSwitcher name={store.name} d={group} />}
          <nav className={menuOpen ? "open" : ""} ref={navRef} data-testid="main-nav">
            {isAdmin ? (
              <Link to="/owner" className={isActive("/owner") ? "active" : ""} data-testid="nav-owner">
                Butikker
              </Link>
            ) : (
              <>
                {NAV.map(([href, label]) => (
                  <Link key={href} to={href} className={isActive(href) ? "active" : ""} aria-current={isActive(href) ? "page" : undefined} data-testid={`nav-${label}`}>
                    {label}
                  </Link>
                ))}
                {secondary.map((i) =>
                  i.external ? (
                    <a key={i.href} href={i.href} target="_blank" rel="noreferrer" className="nav-sec" data-testid={i.testid}>{i.label}</a>
                  ) : (
                    <Link key={i.href} to={i.href} className={"nav-sec" + (isActive(i.href) ? " active" : "")} aria-current={isActive(i.href) ? "page" : undefined} data-testid={i.testid}>
                      {i.label}
                      {i.dot && <span className="nav-dot" aria-label="Facebook-forbindelsen er afbrudt" data-testid="nav-settings-dot" />}
                    </Link>
                  )
                )}
              </>
            )}
          </nav>
          {!isAdmin && <NavMore items={secondary} onLogout={doLogout} dot={secondary.some((i) => i.dot)} />}
          <span className="spacer" />
          <InstallButton />
          {!isAdmin && store?.test_mode && (
            <Link className="demo-flag" to="/settings" title="Der sendes intet til Facebook, før du forbinder din side">
              Testtilstand
            </Link>
          )}
          <button
            className="logout"
            title="Log ud"
            data-testid="logout-btn"
            onClick={doLogout}
          >
            Log ud
          </button>
          <button
            className="nav-toggle"
            aria-label="Menu"
            aria-expanded={menuOpen}
            data-testid="nav-toggle"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>
      {menuOpen && <div className="nav-scrim" onClick={() => setMenuOpen(false)} data-testid="nav-scrim" />}
      {store && !isAdmin && <Banner store={store} path={active} />}
      {!isAdmin && <InstallBanner />}
      <main>{netError ? <NetErrorState onRetry={retry} /> : children}</main>
      {!isAdmin && <BottomNav />}
      <IosInstallSheet />
    </div>
  );
}
